import React, { useState, createContext, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './Auth';
import Header from './components/Header';
import Footer from './components/Footer';
import Notification from './components/Notification';
import { NotificationProvider } from './components/NotificationContext';
import { useRequireAuth } from './hooks/useRequireAuth';

// Lazy load components that aren't needed immediately
const HomePage = lazy(() => import('./components/HomePage'));
const EventList = lazy(() => import('./events/EventList'));
const EventFormManager = lazy(() => import('./events/EventFormManager'));
const EventDetailMain = lazy(() => import('./events/EventDetailMain'));
const Login = lazy(() => import('./users/Login'));
const AdminLogin = lazy(() => import('./users/AdminLogin'));
const Profile = lazy(() => import('./users/Profile'));
const MyEvents = lazy(() => import('./events/MyEvents'));
const CreateTestUser = lazy(() => import('./users/CreateTestUser'));
const Whoops = lazy(() => import('./components/Whoops'));
const ICSFileDownload = lazy(() => import('./components/ICSFileDownload'));
const MyGroups = lazy(() => import('./groups/MyGroups'));
const GroupPage = lazy(() => import('./groups/GroupPage'));

export const PendingActionContext = createContext(null);

// Update ProtectedRoute to allow all authenticated users
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/whoops" />;
  }

  return children;
};

function App() {
  const [pendingAction, setPendingAction] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    timeFrame: 'all',
    myEvents: false,
    rsvpd: false,
  });
  const [sortOrder, setSortOrder] = useState('desc');
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);

  const FilterSection = () => (
    <EventList.FilterSection
      activeFilters={activeFilters}
      setActiveFilters={setActiveFilters}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
    />
  );

  return (
    <NotificationProvider>
      <Router>
        <AuthProvider>
          <PendingActionContext.Provider value={{ pendingAction, setPendingAction }}>
            <div className="flex flex-col min-h-screen bg-gray-100 font-sans">
              <Header 
                searchTerm={searchTerm} 
                setSearchTerm={setSearchTerm} 
                isFilterExpanded={isFilterExpanded} 
                setIsFilterExpanded={setIsFilterExpanded}
                FilterSection={FilterSection}
                onVisibilityChange={setIsHeaderVisible}
              />
              <Notification />
              <main className={`flex-grow transition-all duration-300 ${isHeaderVisible ? 'mt-16' : 'mt-0'}`}>
                <Suspense fallback={
                  <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="animate-pulse text-gray-400 text-lg">Loading...</div>
                  </div>
                }>
                  <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/events" element={
                    <EventList 
                      searchTerm={searchTerm} 
                      setSearchTerm={setSearchTerm} 
                      isFilterExpanded={isFilterExpanded} 
                      setIsFilterExpanded={setIsFilterExpanded}
                      activeFilters={activeFilters}
                      setActiveFilters={setActiveFilters}
                      sortOrder={sortOrder}
                      setSortOrder={setSortOrder}
                    />
                  } />
                  <Route path="/login" element={<Login />} />
                  <Route path="/admin-login" element={<AdminLogin />} />
                  <Route 
                    path="/profile" 
                    element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/my-events" 
                    element={
                      <ProtectedRoute>
                        <MyEvents />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/create-event" 
                    element={
                      <ProtectedRoute>
                        <EventFormManager />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/manage-event" 
                    element={
                      <ProtectedRoute>
                        <EventFormManager />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/manage-event/:eventUrl" 
                    element={
                      <ProtectedRoute>
                        <EventFormManager />
                      </ProtectedRoute>
                    } 
                  />
                  <Route path="/event/:eventUrl" element={<EventDetailMain />} />
                  <Route path="/event/:eventUrl/ics" element={<ICSFileDownload />} />
                  <Route path="/whoops" element={<Whoops />} />
                  <Route path="/my-groups" element={<MyGroups />} />
                  <Route path="/group/:groupUrl" element={<GroupPage />} />
                </Routes>
                </Suspense>
              </main>
              <Footer />
            </div>
          </PendingActionContext.Provider>
        </AuthProvider>
      </Router>
    </NotificationProvider>
  );
}

export default App;
